import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Loading from 'components/loading_item/Loading';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import { VehicleContext } from 'contexts/vehicles_context/VehiclesContext';
import ConsumptionCard from 'components/consumption_card/ConsumptionCard';
import VehicleComplaintHistory from 'components/vehicle_complaint_history/VehicleComplaintHistory';
import './SingleVehicle.css';
import { themeContext } from 'contexts/theme_context/ThemeContext';
import { languageContext } from 'contexts/language_context/LanguageContext';

export default function SingleVehicle() {
  const location = useLocation();
  const { id } = useParams();
  const { readCookie } = useContext(AuthContext);
  const { vehWithComp, refreshVehicles } = useContext(VehicleContext);
  const { webtheme } = useContext(themeContext);
  const { weblanguage, jsonfile } = useContext(languageContext);
  const copy = jsonfile?.[weblanguage]?.singleVehicle || {};
  const routeStateVehicle = location.state?.chassis_number ? location.state : null;
  const routeNotice = location.state?.notice;
  const [vehicleData, setVehicleData] = useState();
  const [loadError, setLoadError] = useState('');
  const [successNotice, setSuccessNotice] = useState(routeNotice === 'complaint-submitted');

  useEffect(() => {
    if (routeNotice === 'complaint-submitted') {
      setSuccessNotice(true);
    }
  }, [routeNotice]);

  useEffect(() => {
    let isMounted = true;

    const resolveVehicle = async () => {
      if (!id && !routeStateVehicle) {
        setVehicleData(null);
        setLoadError(copy.vehicleNotFound || 'Vehicle was not found.');
        return;
      }

      if (routeStateVehicle) {
        setVehicleData(routeStateVehicle);
        setLoadError('');
        return;
      }

      const foundVehicle = vehWithComp.find(v => v.chassis_number === id);
      if (foundVehicle) {
        setVehicleData(foundVehicle);
        setLoadError('');
        return;
      }

      if (vehWithComp.length > 0) {
        setVehicleData(null);
        setLoadError(copy.vehicleNotFound || 'Vehicle was not found.');
        return;
      }

      try {
        const vehicles = await refreshVehicles(readCookie());
        if (!isMounted) return;
        const refreshedVehicle = vehicles.find(v => v.chassis_number === id);
        setVehicleData(refreshedVehicle || null);
        setLoadError(refreshedVehicle ? '' : copy.vehicleNotFound || 'Vehicle was not found.');
      } catch {
        if (!isMounted) return;
        setVehicleData(null);
        setLoadError(copy.loadError || 'Unable to load vehicle details.');
      }
    };

    resolveVehicle();
    return () => {
      isMounted = false;
    };
  }, [copy.loadError, copy.vehicleNotFound, id, readCookie, refreshVehicles, routeStateVehicle, vehWithComp]);

  const renderEfficiencyHistory = () => {
    if (vehicleData && vehicleData.dated_eff) {
      const consumptionEntries = Object.entries(vehicleData.dated_eff).sort((a, b) => new Date(b[0]) - new Date(a[0])); // Sort descending by date

      if (consumptionEntries.length === 0) return <p className="text-muted text-center py-4">{copy.noConsumptionHistory || 'No consumption history available.'}</p>;

      return consumptionEntries.map(([date, data], index) => {
        const { efficiency, dailyMileage } = data;
        return (
          <ConsumptionCard
            key={index}
            date={date}
            efficiency={efficiency}
            dailyMileage={dailyMileage}
          />
        );
      });
    }
    return <p className="text-muted text-center py-4">{copy.noConsumptionHistory || 'No consumption history available.'}</p>;
  };

  return (
    <div className={`container pb-5`}>
      {vehicleData ? (
        <div className='mt-5 d-flex flex-wrap flex-column'>
          {successNotice && (
            <div className={`single-vehicle-notice single-vehicle-notice--success ${webtheme}-theme`} role="status" aria-live="polite">
              {copy.successNotice || 'Complaint submitted. Aftersales has been notified.'}
            </div>
          )}

          {/* Basic Info Card */}
          <div className={`card shadow-sm p-4 mb-5 ${webtheme}-theme`} style={{ borderRadius: '8px', border: 'none' }}>
            <h4 className="mb-4 text-orange">{copy.vehicleDetails || 'Vehicle Details'}</h4>
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className={`d-flex align-items-center mb-2`}>
                  <p className={`text-muted m-0 mx-1 fw-bold`} style={{ width: '120px' }}>{copy.chassisNumber || 'Chassis Number'}</p>
                  <span>:</span>
                  <p className={`fs-6 m-0 mx-2 fw-semibold`}> {vehicleData['chassis_number']}</p>
                </div>
                <div className={`d-flex align-items-center mb-2`}>
                  <p className={`text-muted m-0 mx-1 fw-bold`} style={{ width: '120px' }}>{copy.licensePlate || 'License Plate'}</p>
                  <span>:</span>
                  <p className={`fs-6 m-0 mx-2 fw-semibold`}> {vehicleData['license_plate']}</p>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className={`d-flex align-items-center mb-2`}>
                  <p className={`text-muted m-0 mx-1 fw-bold`} style={{ width: '120px' }}>{copy.vehicleMake || 'Vehicle Make'}</p>
                  <span>:</span>
                  <p className={`fs-6 m-0 mx-2 fw-semibold`}> {vehicleData['vehicle_make']}</p>
                </div>
                <div className={`d-flex align-items-center mb-2`}>
                  <p className={`text-muted m-0 mx-1 fw-bold`} style={{ width: '120px' }}>{copy.status || 'Status'}</p>
                  <span>:</span>
                  <p className={`fs-6 m-0 mx-2 fw-semibold text-${vehicleData['vehicle_status'] === 'Operational' ? 'success' : 'danger'}`}> {copy.statuses?.[vehicleData['vehicle_status']] || vehicleData['vehicle_status']}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Consumption History Column */}
            <div className="col-lg-4 mb-4">
              <div className={`card shadow-sm p-4 h-100 ${webtheme}-theme`} style={{ borderRadius: '8px', border: 'none' }}>
                <h5 className="section-title text-orange">{copy.consumptionHistory || 'Consumption History'}</h5>
                <div className="efficiency-card-container">
                  {renderEfficiencyHistory()}
                </div>
              </div>
            </div>

            {/* Complaints Column */}
            <div className="col-lg-8 mb-4">
              <div className={`card shadow-sm p-4 h-100 ${webtheme}-theme`} style={{ borderRadius: '8px', border: 'none' }}>
                <h5 className="section-title text-orange">{copy.complaints || 'Complaints'}</h5>
                <VehicleComplaintHistory chassisNumber={vehicleData.chassis_number} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className='vh-100 d-flex justify-content-center align-items-center'>
          {loadError ? (
            <div className="alert alert-danger" role="alert">{loadError}</div>
          ) : (
            <Loading />
          )}
        </div>
      )}
    </div>
  );
}
